import { createApi } from "@reduxjs/toolkit/query/react";
import { adminBaseQuery } from "@store/baseQuery";
import { LookupStatus } from "@/utils/lookupStatus";
import { ConditionLookup } from "@entities/conditions/api";

// Type definitions based on swagger schema
export interface ConditionGroupLookup {
  id: number;
  name: string;
  description: string | null;
  order: number | null;
  status: LookupStatus;
  created_at: string;
  updated_at: string;
}

export interface ConditionGroupLookupsResponse {
  data: ConditionGroupLookup[];
}

export interface CreateConditionGroupLookupRequest {
  name: string;
  description?: string | null;
  order?: number | null;
  status: LookupStatus;
}

export interface UpdateConditionGroupLookupRequest {
  name: string;
  description?: string | null;
  order?: number | null;
  status?: LookupStatus;
}

export interface AssignConditionsRequest {
  condition_ids: number[];
}

export interface ErrorResponse {
  status: boolean;
  message: string;
  errors?: Record<string, string[]>;
}

export const conditionGroupsLookupsApi = createApi({
  reducerPath: "conditionGroupsLookupsApi",
  baseQuery: adminBaseQuery,
  tagTypes: ["ConditionGroupLookup"],
  endpoints: (builder) => ({
    // Get all condition groups lookups
    getConditionGroupsLookups: builder.query<ConditionGroupLookupsResponse, void>({
      query: () => "/condition-lookup-groups",
      providesTags: ["ConditionGroupLookup"],
    }),

    // Get a specific condition groups lookup
    getConditionGroupLookup: builder.query<ConditionGroupLookup, number>({
      query: (id) => `/condition-lookup-groups/${id}`,
      providesTags: (result, error, id) => [
        { type: "ConditionGroupLookup", id },
      ],
    }),

    // Create a new condition groups lookup
    createConditionGroupLookup: builder.mutation<
      ConditionGroupLookup,
      CreateConditionGroupLookupRequest
    >({
      query: (data) => ({
        url: "/condition-lookup-groups",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ConditionGroupLookup"],
    }),

    // Update a specific condition groups lookup
    updateConditionGroupLookup: builder.mutation<
      ConditionGroupLookup,
      { id: number; data: UpdateConditionGroupLookupRequest }
    >({
      query: ({ id, data }) => ({
        url: `/condition-lookup-groups/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ConditionGroupLookup", id },
        "ConditionGroupLookup",
      ],
    }),

    // Delete a specific condition groups lookup
    deleteConditionGroupLookup: builder.mutation<
      { condition_lookup_group_id: number },
      number
    >({
      query: (id) => ({
        url: `/condition-lookup-groups/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ConditionGroupLookup"],
    }),

    // Get all conditions assigned to a condition groups lookup
    getConditionGroupConditions: builder.query<ConditionLookup[], number>({
      query: (id) => `/condition-lookup-groups/${id}/conditions`,
      providesTags: (result, error, id) => [
        { type: "ConditionGroupLookup", id: `${id}-conditions` },
      ],
    }),

    // Assign conditions to a condition groups lookup
    assignConditionsToGroup: builder.mutation<
      ConditionLookup[],
      { id: number; data: AssignConditionsRequest }
    >({
      query: ({ id, data }) => ({
        url: `/condition-lookup-groups/${id}/conditions`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ConditionGroupLookup", id: `${id}-conditions` },
        "ConditionGroupLookup",
      ],
    }),

    // Bulk change status
    bulkChangeStatus: builder.mutation<
      { message: string; updated_count: number; failed_count: number; updated_ids: number[] },
      { ids: number[]; status: LookupStatus }
    >({
      query: (data) => ({
        url: "/condition-lookup-groups/bulk-status",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ConditionGroupLookup"],
    }),
  }),
});

export const {
  useGetConditionGroupsLookupsQuery,
  useGetConditionGroupLookupQuery,
  useCreateConditionGroupLookupMutation,
  useUpdateConditionGroupLookupMutation,
  useDeleteConditionGroupLookupMutation,
  useGetConditionGroupConditionsQuery,
  useAssignConditionsToGroupMutation,
  useBulkChangeStatusMutation,
} = conditionGroupsLookupsApi; 